import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { triggerMilestoneCheck } from '../lib/emailEvents';
import { getAllStickerCodes, getAllTradeableStickerCodes, TOTAL_STICKERS, getTeamStickerCodes } from '../data/stickers';
import { recomputeFeedEvents, maybeGenerateWishlistMatch } from '../lib/feedEvents';
import { checkAchievements } from '../lib/achievements';

export interface StickerStats {
  owned: number;
  missing: number;
  duplicates: number;
  total: number;
  percentage: number;
  ccOwned: number;
}

export function useStickers(userId: string) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const countsRef = useRef(counts);
  countsRef.current = counts;
  const milestoneBucketRef = useRef<number>(0);

  const allCodes = useMemo(() => getAllStickerCodes(), []);
  const tradeableCodes = useMemo(() => getAllTradeableStickerCodes(), []);

  const maybeTriggerMilestone = useCallback(
    (newCounts: Record<string, number>) => {
      const owned = allCodes.filter((c) => (newCounts[c] || 0) >= 1).length;
      const percentage = TOTAL_STICKERS > 0 ? (owned / TOTAL_STICKERS) * 100 : 0;
      const bucket = Math.floor(percentage / 25);
      if (bucket > milestoneBucketRef.current && bucket >= 1) {
        milestoneBucketRef.current = bucket;
        void triggerMilestoneCheck(userId, percentage);
      } else {
        milestoneBucketRef.current = bucket;
      }
    },
    [allCodes, userId]
  );

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('stickers')
        .select('sticker_code, count')
        .eq('user_id', userId);

      if (data) {
        const map: Record<string, number> = {};
        data.forEach((row: { sticker_code: string; count: number }) => {
          map[row.sticker_code] = row.count;
        });
        setCounts(map);
        const owned = allCodes.filter((c) => (map[c] || 0) >= 1).length;
        const pct = TOTAL_STICKERS > 0 ? (owned / TOTAL_STICKERS) * 100 : 0;
        milestoneBucketRef.current = Math.floor(pct / 25);
      }
      setLoading(false);
    };
    load();
  }, [userId, allCodes]);

  const handleClick = useCallback(
    async (code: string) => {
      const current = countsRef.current[code] || 0;
      const next = current >= 99 ? 0 : current + 1;

      const updated = { ...countsRef.current, [code]: next };
      setCounts(updated);

      if (next === 0) {
        await supabase
          .from('stickers')
          .delete()
          .eq('user_id', userId)
          .eq('sticker_code', code);
      } else {
        await supabase.from('stickers').upsert(
          {
            user_id: userId,
            sticker_code: code,
            count: next,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,sticker_code' }
        );
      }
      maybeTriggerMilestone(updated);
      // Background: feed + conquistas + daily activity (falha silenciosa)
      void recomputeFeedEvents(userId);
      void checkAchievements(userId);
      // Virou repetida (1 → 2): notifica amigos com wishlist
      if (current === 1 && next === 2) {
        void maybeGenerateWishlistMatch(userId, code);
      }
      void supabase
        .from('daily_activity')
        .insert({ user_id: userId, date: new Date().toISOString().slice(0, 10) })
        .then(({ error }) => {
          if (error && error.code !== '23505') console.error('daily_activity insert', error);
        });
    },
    [userId, maybeTriggerMilestone]
  );

  const handleDecrement = useCallback(
    async (code: string) => {
      const current = countsRef.current[code] || 0;
      if (current === 0) return;
      const next = current - 1;

      setCounts((prev) => ({ ...prev, [code]: next }));

      if (next === 0) {
        await supabase.from('stickers').delete().eq('user_id', userId).eq('sticker_code', code);
      } else {
        await supabase.from('stickers').upsert(
          { user_id: userId, sticker_code: code, count: next, updated_at: new Date().toISOString() },
          { onConflict: 'user_id,sticker_code' }
        );
      }
    },
    [userId]
  );

  const bulkUpdate = useCallback(
    async (updates: Array<{ code: string; count: number }>): Promise<{ applied: number }> => {
      if (updates.length === 0) return { applied: 0 };

      const now = new Date().toISOString();
      const { error } = await supabase
        .from('stickers')
        .upsert(
          updates.map((u) => ({
            user_id: userId,
            sticker_code: u.code,
            count: u.count,
            updated_at: now,
          })),
          { onConflict: 'user_id,sticker_code' }
        );

      if (error) throw new Error(error.message);

      const newCounts = { ...countsRef.current };
      updates.forEach((u) => {
        newCounts[u.code] = u.count;
      });
      setCounts(newCounts);

      maybeTriggerMilestone(newCounts);
      void recomputeFeedEvents(userId);
      void checkAchievements(userId);

      return { applied: updates.length };
    },
    [userId, maybeTriggerMilestone]
  );

  const resetDuplicates = useCallback(async (): Promise<Map<string, number>> => {
    const snapshot = new Map<string, number>();
    const now = new Date().toISOString();
    const updates: Array<{ user_id: string; sticker_code: string; count: number; updated_at: string }> = [];

    for (const [code, count] of Object.entries(countsRef.current)) {
      if (count > 1) {
        snapshot.set(code, count);
        updates.push({ user_id: userId, sticker_code: code, count: 1, updated_at: now });
      }
    }

    if (updates.length === 0) return snapshot;

    const { error } = await supabase
      .from('stickers')
      .upsert(updates, { onConflict: 'user_id,sticker_code' });

    if (error) throw new Error(error.message);

    const newCounts = { ...countsRef.current };
    snapshot.forEach((_, code) => {
      newCounts[code] = 1;
    });
    setCounts(newCounts);

    return snapshot;
  }, [userId]);

  const restoreFromSnapshot = useCallback(async (snapshot: Map<string, number>): Promise<void> => {
    if (snapshot.size === 0) return;

    const now = new Date().toISOString();
    const updates = Array.from(snapshot.entries()).map(([code, count]) => ({
      user_id: userId,
      sticker_code: code,
      count,
      updated_at: now,
    }));

    const { error } = await supabase
      .from('stickers')
      .upsert(updates, { onConflict: 'user_id,sticker_code' });

    if (error) throw new Error(error.message);

    const newCounts = { ...countsRef.current };
    snapshot.forEach((count, code) => {
      newCounts[code] = count;
    });
    setCounts(newCounts);
  }, [userId]);

  const ccCodes = useMemo(() => getTeamStickerCodes('CC'), []);

  const stats: StickerStats = useMemo(() => {
    const owned = allCodes.filter((c) => (counts[c] || 0) >= 1).length;
    const duplicates = allCodes.filter((c) => (counts[c] || 0) >= 2).length;
    const ccOwned = ccCodes.filter((c) => (counts[c] || 0) >= 1).length;
    return {
      owned,
      missing: TOTAL_STICKERS - owned,
      duplicates,
      total: TOTAL_STICKERS,
      percentage: TOTAL_STICKERS > 0 ? (owned / TOTAL_STICKERS) * 100 : 0,
      ccOwned,
    };
  }, [counts, allCodes, ccCodes]);

  return { counts, loading, handleClick, handleDecrement, bulkUpdate, resetDuplicates, restoreFromSnapshot, stats, allCodes, tradeableCodes };
}
